import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Navbar from './Navbar';
import Footer from './Footer';
import Login from './Login';
import { FaShoppingCart, FaTrash, FaTag, FaArrowLeft, FaSpinner, FaLayerGroup, FaShippingFast, FaTruck, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const BASE = 'https://evercorenutrition.com';
const GUEST_CART_KEY = 'guest_cart';

const Cart = () => {
  const navigate = useNavigate();
  const [cartData, setCartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatingItems, setUpdatingItems] = useState({});

  const [couponCode, setCouponCode] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [showAvailableCoupons, setShowAvailableCoupons] = useState(false);

  const [shippingCharge, setShippingCharge] = useState(0);
  const [loadingShipping, setLoadingShipping] = useState(false);
  const [freeShippingData, setFreeShippingData] = useState(null);
  
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [pendingCheckout, setPendingCheckout] = useState(false);

  const getCustomerId = useCallback(() => {
    const userData = localStorage.getItem('userData');
    if (userData) {
      try {
        const parsed = JSON.parse(userData);
        return parsed.id || parsed.customer_id || null;
      } catch {
        return null;
      }
    }
    return null;
  }, []);

  const isAuthenticated = useCallback(() => {
    return !!localStorage.getItem('authToken');
  }, []);

  // Guest cart functions
  const getGuestCart = useCallback(() => {
    try {
      const cart = localStorage.getItem(GUEST_CART_KEY);
      return cart ? JSON.parse(cart) : [];
    } catch (error) {
      console.error('Error getting guest cart:', error);
      return [];
    }
  }, []);

  const saveGuestCart = useCallback((cartItems) => {
    try {
      localStorage.setItem(GUEST_CART_KEY, JSON.stringify(cartItems));
    } catch (error) {
      console.error('Error saving guest cart:', error);
    }
  }, []);

  const clearGuestCart = useCallback(() => {
    try {
      localStorage.removeItem(GUEST_CART_KEY);
    } catch (error) {
      console.error('Error clearing guest cart:', error);
    }
  }, []);

  // FIXED: Merge guest cart with user cart - preserves all item data
  const mergeGuestCartWithUserCart = useCallback(async (customerId, token) => {
    const guestCart = getGuestCart();
    if (!guestCart || guestCart.length === 0) return { success: true, mergedCount: 0 };

    let mergedCount = 0;
    let errors = [];

    try {
      for (const item of guestCart) {
        // Prepare request body based on item type
        const requestBody = {
          customer_id: String(customerId),
          quantity: item.quantity
        };

        if (item.item_type === 'combo') {
          requestBody.combo_id = item.combo_id;
        } else {
          requestBody.product_id = item.product_id;
          if (item.variant_id && item.variant_id !== 'null' && item.variant_id !== null) {
            requestBody.variant_id = item.variant_id;
          }
        }

        console.log('Merging item:', requestBody);

        const response = await fetch('https://evercorenutrition.com/ev_admin/api/cart/', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody)
        });
        
        if (response.ok) {
          mergedCount++;
        } else {
          const errorData = await response.json().catch(() => ({}));
          errors.push({ item, error: errorData });
        }
      }
      
      if (mergedCount > 0) {
        clearGuestCart();
      }
      
      return { success: true, mergedCount, errors };
    } catch (error) {
      console.error('Error merging guest cart:', error);
      return { success: false, message: 'Failed to merge guest cart', errors };
    }
  }, [getGuestCart, clearGuestCart]);

  // FIXED: Subtotal calculation with proper price validation
  const subtotal = useMemo(() => {
    if (!cartData?.items?.length) return 0;
    
    let total = 0;
    for (const item of cartData.items) {
      let unitPrice = item.unit_price;
      
      if (typeof unitPrice !== 'number' || isNaN(unitPrice) || unitPrice <= 0) {
        unitPrice = parseFloat(item.price || item.selling_price || item.unit_price || 0);
      }
      
      if (typeof unitPrice !== 'number' || isNaN(unitPrice)) {
        unitPrice = 0;
      }
      
      const quantity = typeof item.quantity === 'number' ? item.quantity : parseInt(item.quantity || 0);
      total += unitPrice * quantity;
    }
    
    return total;
  }, [cartData]);

  const freeShippingThreshold = useMemo(() => {
    const freeShipping = freeShippingData?.free_shipping_options?.find(
      o => o.status === 'active' && o.shipping_charge === '0.00'
    );
    if (!freeShipping) return null;
    const min = parseFloat(freeShipping.min_cart_value);
    return isNaN(min) ? null : min;
  }, [freeShippingData]);

  const qualifiesForFreeShipping = useMemo(() => {
    if (!freeShippingThreshold || subtotal < freeShippingThreshold) return false;
    return true;
  }, [subtotal, freeShippingThreshold]);

  const amountToFreeShipping = useMemo(() => {
    if (!freeShippingThreshold || subtotal >= freeShippingThreshold) return 0;
    return Math.max(freeShippingThreshold - subtotal, 0);
  }, [subtotal, freeShippingThreshold]);

 
  //   if (!appliedCoupon) return 0;
  //   if (appliedCoupon.discount_type === 'percentage') {
  //     return (subtotal * appliedCoupon.discount_value) / 100;
  //   } else if (appliedCoupon.discount_type === 'amount') {
  //     return appliedCoupon.discount_value;
  //   }
  //   return 0;
  // }, [appliedCoupon, subtotal]);

const discountAmount = useMemo(() => {

  if (!appliedCoupon || !cartData?.items?.length) {
    return 0;
  }

  const applyOn = String(
    appliedCoupon.apply_on || ''
  ).trim().toLowerCase();

  let eligibleSubtotal = 0;

  // PRODUCT ONLY
  if (applyOn === 'product') {

    eligibleSubtotal = cartData.items
      .filter(item => {
        const type = String(
          item.item_type ||
          (item.combo_id ? 'combo' : 'product')
        ).trim().toLowerCase();

        return type === 'product';
      })
      .reduce((sum, item) => {
        return sum + ((item.unit_price || 0) * item.quantity);
      }, 0);

  }

  // COMBO ONLY
  else if (applyOn === 'combo') {

    eligibleSubtotal = cartData.items
      .filter(item => {
        const type = String(
          item.item_type ||
          (item.combo_id ? 'combo' : 'product')
        ).trim().toLowerCase();

        return type === 'combo';
      })
      .reduce((sum, item) => {
        return sum + ((item.unit_price || 0) * item.quantity);
      }, 0);

  }

  // MIXED CART ONLY
  else if (applyOn === 'cart') {

    eligibleSubtotal = subtotal;

  }

  if (appliedCoupon.discount_type === 'percentage') {
    const percentageDiscount = (
      eligibleSubtotal *
      appliedCoupon.discount_value
    ) / 100;

    return appliedCoupon.max_discount_amount
      ? Math.min(percentageDiscount, appliedCoupon.max_discount_amount)
      : percentageDiscount;
  }

  return Math.min(
    appliedCoupon.discount_value || 0,
    eligibleSubtotal
  );

}, [appliedCoupon, cartData, subtotal]);
  const total = useMemo(() => {
    const calculatedTotal = subtotal - discountAmount + shippingCharge;
    return calculatedTotal < 0 ? 0 : calculatedTotal;
  }, [subtotal, discountAmount, shippingCharge]);

  const cartItemCount = useMemo(() => cartData?.total_items || 0, [cartData]);

const getCartType = (cartItems) => {

  if (!cartItems || cartItems.length === 0) {
    return null;
  }

  const hasCombo = cartItems.some(item => {
    const type = String(
      item.item_type ||
      (item.combo_id ? 'combo' : 'product')
    ).trim().toLowerCase();

    return type === 'combo';
  });

  const hasProduct = cartItems.some(item => {
    const type = String(
      item.item_type ||
      (item.combo_id ? 'combo' : 'product')
    ).trim().toLowerCase();

    return type === 'product';
  });

  // MIX CART
  if (hasCombo && hasProduct) {
    return 'mixed';
  }

  // ONLY COMBO
  if (hasCombo) {
    return 'combo';
  }

  // ONLY PRODUCT
  if (hasProduct) {
    return 'product';
  }

  return null;
};
  // FIXED: Check if coupon is valid for current cart items
  // const isCouponValidForCart = useCallback((coupon, cartItems) => {
  //   if (!coupon || !cartItems?.length) return false;

  //   const applyOn = coupon.apply_on || coupon.target_type;

  //   if (applyOn === 'combo') {
  //     const hasNonCombo = cartItems.some(item => item.item_type !== 'combo');
  //     if (hasNonCombo) return false;
  //   }

  //   if (applyOn === 'product') {
  //     const hasCombo = cartItems.some(item => item.item_type === 'combo');
  //     if (hasCombo) return false;
  //   }

  //   return true;
  // }, []);

//   const isCouponValidForCart = useCallback((coupon, cartItems) => {
//   if (!coupon || !cartItems?.length) return false;

//   const cartType = getCartType(cartItems);
//   const applyOn = coupon.apply_on || coupon.target_type || 'cart';

//   // 🔹 Combo coupon → only combo cart
//   if (applyOn === 'combo') {
//     return cartType === 'combo';
//   }

//   // 🔹 Product coupon → only product cart
//   if (applyOn === 'product') {
//     return cartType === 'product';
//   }

//   // 🔹 Full cart coupon → only mixed cart
//   if (applyOn === 'cart') {
//     return cartType === 'mixed';
//   }

//   return false;
// }, []);


const isCouponValidForCart = useCallback((coupon, cartItems) => {

  if (!coupon || !cartItems?.length) {
    return false;
  }

  const cartType = getCartType(cartItems);

  const applyOn = String(
    coupon.apply_on ||
    coupon.target_type ||
    'cart'
  ).trim().toLowerCase();

  console.log("CART TYPE =>", cartType);
  console.log("COUPON TYPE =>", applyOn);

  // PRODUCT coupon
  // ONLY when cart has ONLY products
  if (applyOn === 'product') {
    return cartType === 'product';
  }

  // COMBO coupon
  // ONLY when cart has ONLY combos
  if (applyOn === 'combo') {
    return cartType === 'combo';
  }

  // CART coupon
  // ONLY when cart has BOTH combo + product
  if (applyOn === 'cart') {
    return cartType === 'mixed';
  }

  return false;

}, []);

  // FIXED: Check if coupon meets minimum order amount
  const isCouponMinAmountMet = useCallback((coupon, cartTotal) => {
    if (!coupon) return false;
    const minAmount = coupon.min_order_amount || 0;
    return cartTotal >= minAmount;
  }, []);

  // FIXED: Validate coupon against current cart state
  const validateCouponAgainstCart = useCallback((coupon, cartItems, cartTotal) => {
    if (!coupon) return { valid: false, reason: 'No coupon applied' };
    
    // Check minimum order amount
    if (!isCouponMinAmountMet(coupon, cartTotal)) {
      return { 
        valid: false, 
        reason: `Minimum order amount of ₹${(coupon.min_order_amount || 0).toFixed(2)} required. Current subtotal: ₹${cartTotal.toFixed(2)}` 
      };
    }
    
    // Check if coupon is valid for cart items
    if (!isCouponValidForCart(coupon, cartItems)) {
      return { 
        valid: false, 
        reason: coupon.apply_on === 'combo' 
          ? 'This coupon is applicable only on combo items.' 
          : coupon.apply_on === 'product'
          ? 'This coupon is applicable only on products.'
          : 'This coupon is not applicable to your current cart items.'
      };
    }
    
    return { valid: true, reason: null };
  }, [isCouponMinAmountMet, isCouponValidForCart]);

  // FIXED: Auto-remove coupon if conditions are no longer met
  const checkAndAutoRemoveCoupon = useCallback(async () => {
    if (!appliedCoupon) return;
    
    const validation = validateCouponAgainstCart(appliedCoupon, cartData?.items || [], subtotal);
    
    if (!validation.valid) {
      console.log('Auto-removing coupon:', validation.reason);
      setAppliedCoupon(null);
      sessionStorage.removeItem('appliedCoupon');
      setCouponError(validation.reason);
      
      // Clear error after 5 seconds
      setTimeout(() => {
        setCouponError('');
      }, 5000);
    }
  }, [appliedCoupon, cartData, subtotal, validateCouponAgainstCart]);

  const resolveCartItemIdentifier = useCallback((item) => {
    if (!item) return null;
    return item.id || item.cart_item_id || item.item_id || item.variant_id || item.product_id || null;
  }, []);

  const resolveFrequentlyId = useCallback((item) => {
    if (item?.combo_snapshot?.source === 'frequently_bought') {
      return item.combo_snapshot.frequently_id || null;
    }
    return item?.frequently_id || null;
  }, []);

  const normalizeCart = (cart) => {
    if (!cart || !cart.items) return cart;
    return {
      ...cart,
      items: cart.items.map(item => {
        let unitPrice = parseFloat(item.unit_price || item.price || 0);
        if (isNaN(unitPrice) || unitPrice < 0) {
          unitPrice = 0;
        }
        const resolvedId = resolveCartItemIdentifier(item) || `${item.product_id || item.combo_id || 'item'}-${item.variant_id || item.combo_id || 'default'}`;
        return {
          ...item,
          id: resolvedId,
          unit_price: unitPrice,
          item_type:
            item.item_type ||
            (item.combo_id ? 'combo' : 'product'),
                  };
      }),
    };
  };

  const fetchAvailableCoupons = useCallback(async () => {
    try {
      const response = await fetch('https://evercorenutrition.com/ev_admin/api/promos/');
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.promocodes) {
          const now = new Date();
          
          const validCoupons = data.promocodes.filter(coupon => {
            if (coupon.discount_value <= 0) return false;
            if (coupon.status === 'inactive' || coupon.is_active === false) return false;
            
            if (coupon.valid_from && coupon.valid_to) {
              const validFrom = new Date(coupon.valid_from);
              const validTo = new Date(coupon.valid_to);
              if (now < validFrom || now > validTo) return false;
            }
            
            if (coupon.remaining_uses !== null && coupon.remaining_uses !== undefined && coupon.remaining_uses <= 0) return false;
            
            return true;
          });
          
          setAvailableCoupons(validCoupons);
        }
      }
    } catch (err) {
      console.error('Error fetching coupons:', err);
    }
  }, []);

  // FIXED: fetchCart - properly handles guest cart and user cart
  const fetchCart = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    const customerId = getCustomerId();

    // User is logged in - fetch cart from API
    if (token && customerId) {
      try {
        const response = await fetch(
          `https://evercorenutrition.com/ev_admin/api/cart/?customer_id=${customerId}`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) throw new Error('Failed to fetch cart');

        const data = await response.json();
        if (data.success) {
          const normalizedCart = normalizeCart(data.cart);
          setCartData(normalizedCart);

          const savedCoupon = sessionStorage.getItem('appliedCoupon');
          if (savedCoupon) {
            const parsedCoupon = JSON.parse(savedCoupon);
            const isValid = isCouponValidForCart(parsedCoupon, normalizedCart.items);
            if (isValid) {
              setAppliedCoupon(parsedCoupon);
            } else {
              sessionStorage.removeItem('appliedCoupon');
            }
          }
        } else {
          setError('Could not load cart');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
      return;
    }

    // User is not logged in - load from guest cart
    const guestCart = getGuestCart();
    if (guestCart && guestCart.length > 0) {
      const normalizedCart = {
        items: guestCart.map(item => {
          let unitPrice = parseFloat(item.unit_price || item.price || 0);
          if (isNaN(unitPrice) || unitPrice < 0) unitPrice = 0;
          return {
            ...item,
            id: item.combo_id || item.variant_id || item.product_id,
            unit_price: unitPrice,
            quantity: item.quantity,
            item_type: item.item_type || (item.combo_id ? 'combo' : 'product'),
            product_name: item.name,
            variant_image: item.image,
            product_id: item.product_id,
            variant_id: item.variant_id,
            combo_id: item.combo_id,
          };
        }),
        total_items: guestCart.reduce((sum, item) => sum + item.quantity, 0),
        total_quantity: guestCart.reduce((sum, item) => sum + item.quantity, 0),
      };
      setCartData(normalizedCart);
      setError(null);
    } else {
      setCartData({ items: [], total_items: 0, total_quantity: 0 });
      setError(null);
    }
    setLoading(false);
  }, [getCustomerId, isCouponValidForCart, getGuestCart]);

  const calculateShipping = useCallback(async (cartTotal) => {
    if (!cartTotal || cartTotal <= 0) {
      setShippingCharge(0);
      return;
    }
    setLoadingShipping(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('https://evercorenutrition.com/ev_admin/api/shipping/calculate/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({ subtotal: cartTotal }),
      });
      if (response.ok) {
        const data = await response.json();
        setShippingCharge(parseFloat(data.shipping_charge) || 0);
      }
    } catch (err) {
      console.error('Error calculating shipping:', err);
    } finally {
      setLoadingShipping(false);
    }
  }, []);

  // FIXED: updateQuantity with auto coupon validation
  const updateQuantity = useCallback(async (itemId, newQty) => {
    if (newQty < 1) return;

    const currentItem = cartData?.items?.find(item => {
      const resolvedId = resolveCartItemIdentifier(item);
      return item.id === itemId || item.product_id === itemId || item.variant_id === itemId || resolvedId === itemId;
    });
    if (!currentItem) return;

    const resolvedItemId = resolveCartItemIdentifier(currentItem);
    const frequentlyId = resolveFrequentlyId(currentItem);
    const itemsToUpdate = frequentlyId
      ? cartData.items.filter(item => resolveFrequentlyId(item) === frequentlyId)
      : [currentItem];
    const itemIdsToUpdate = itemsToUpdate.map(item => resolveCartItemIdentifier(item));

    setUpdatingItems(prev => ({
      ...prev,
      ...Object.fromEntries(itemIdsToUpdate.map(id => [id, true])),
    }));
    
    setCartData(prev => {
      if (!prev) return prev;
      const updatedItems = prev.items.map(item => {
        const resolvedMatchId = resolveCartItemIdentifier(item);
        const matches = frequentlyId
          ? resolveFrequentlyId(item) === frequentlyId
          : item.id === itemId || item.product_id === itemId || item.variant_id === itemId || resolvedMatchId === itemId || resolvedMatchId === resolvedItemId;
        if (!matches) return item;
        return { 
          ...item,
          quantity: newQty
        };
      });
      return { ...prev, items: updatedItems };
    });

    const token = localStorage.getItem('authToken');
    const customerId = getCustomerId();

    if (!token || !customerId) {
      const guestCart = getGuestCart();
      const updatedGuestCart = guestCart.map(item => {
        const itemIdentifier = item.combo_id || item.variant_id || item.product_id;
        const keyMatch = frequentlyId
          ? item.frequently_id === frequentlyId
          : itemIdentifier === itemId || (resolvedItemId && (itemIdentifier === resolvedItemId || item.product_id === resolvedItemId || item.variant_id === resolvedItemId));
        if (keyMatch) {
          return { ...item, quantity: newQty };
        }
        return item;
      });
      saveGuestCart(updatedGuestCart);
      
      setTimeout(() => {
        fetchCart();
      }, 100);
      
      setUpdatingItems(prev => {
        const next = { ...prev };
        itemIdsToUpdate.forEach(id => { delete next[id]; });
        return next;
      });
      window.dispatchEvent(new Event('cartUpdated'));
      return;
    }

    try {
      const responses = await Promise.all(itemsToUpdate.map(item => fetch(
        `https://evercorenutrition.com/ev_admin/api/cart/items/${resolveCartItemIdentifier(item)}/`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            customer_id: customerId,
            quantity: newQty,
          }),
        }
      )));

      if (responses.some(response => !response.ok)) {
        console.error('Qty update failed');
      }
      await fetchCart();
    } catch (err) {
      console.error('Failed to update quantity:', err);
      await fetchCart();
    } finally {
      setUpdatingItems(prev => {
        const next = { ...prev };
        itemIdsToUpdate.forEach(id => { delete next[id]; });
        return next;
      });
      window.dispatchEvent(new Event('cartUpdated'));
    }
  }, [getCustomerId, getGuestCart, saveGuestCart, cartData, fetchCart, resolveCartItemIdentifier, resolveFrequentlyId]);

  // FIXED: removeItem with auto coupon validation
  const removeItem = useCallback(async (itemId) => {
    const itemToRemove = cartData?.items?.find(item => {
      const resolvedId = resolveCartItemIdentifier(item);
      return item.id === itemId || item.product_id === itemId || item.variant_id === itemId || resolvedId === itemId;
    });
    const resolvedItemId = resolveCartItemIdentifier(itemToRemove) || itemId;
    const frequentlyId = resolveFrequentlyId(itemToRemove);
    const itemsToRemove = frequentlyId
      ? cartData.items.filter(item => resolveFrequentlyId(item) === frequentlyId)
      : [itemToRemove];
    const itemIdsToRemove = itemsToRemove.map(item => resolveCartItemIdentifier(item));

    setUpdatingItems(prev => ({
      ...prev,
      ...Object.fromEntries(itemIdsToRemove.map(id => [id, true])),
    }));
    const token = localStorage.getItem('authToken');
    const customerId = getCustomerId();

    if (!token || !customerId) {
      const guestCart = getGuestCart();
      const updatedGuestCart = guestCart.filter(item => {
        const itemIdentifier = item.combo_id || item.variant_id || item.product_id;
        const keyMatch = frequentlyId
          ? item.frequently_id === frequentlyId
          : itemIdentifier === itemId || (resolvedItemId && (itemIdentifier === resolvedItemId || item.product_id === resolvedItemId || item.variant_id === resolvedItemId));
        return !keyMatch;
      });
      saveGuestCart(updatedGuestCart);
      
      setCartData(prev => {
        if (!prev) return prev;
        const updatedItems = prev.items.filter(item => {
          const resolvedMatchId = resolveCartItemIdentifier(item);
          const matches = frequentlyId
            ? resolveFrequentlyId(item) === frequentlyId
            : item.id === itemId || item.product_id === itemId || item.variant_id === itemId || resolvedMatchId === itemId || resolvedMatchId === resolvedItemId;
          return !matches;
        });
        return {
          ...prev,
          items: updatedItems,
          total_items: updatedItems.length,
          total_quantity: updatedItems.reduce((sum, item) => sum + item.quantity, 0),
        };
      });
      
      setTimeout(() => {
        fetchCart();
      }, 100);
      
      setUpdatingItems(prev => {
        const next = { ...prev };
        itemIdsToRemove.forEach(id => { delete next[id]; });
        return next;
      });
      window.dispatchEvent(new Event('cartUpdated'));
      return;
    }

    try {
      const responses = await Promise.all(itemsToRemove.map(item => fetch(
        `https://evercorenutrition.com/ev_admin/api/cart/?customer_id=${customerId}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            customer_id: customerId,
            item_id: resolveCartItemIdentifier(item),
          }),
        }
      )));
      const data = responses.length > 0 ? await responses[0].json() : {};

      if (responses.every(response => response.ok) && data.success) {
        setCartData(prev => {
          if (!prev) return prev;
          const updatedItems = prev.items.filter(item => {
            const resolvedMatchId = resolveCartItemIdentifier(item);
            const matches = frequentlyId
              ? resolveFrequentlyId(item) === frequentlyId
              : item.id === itemId || item.product_id === itemId || item.variant_id === itemId || resolvedMatchId === itemId || resolvedMatchId === resolvedItemId;
            return !matches;
          });
          window.dispatchEvent(new Event('cartUpdated'));
          return {
            ...prev,
            items: updatedItems,
            total_items: updatedItems.length,
            total_quantity: updatedItems.reduce((sum, item) => sum + item.quantity, 0),
          };
        });

        await fetchCart();
      }
    } catch (err) {
      console.error('Failed to remove item:', err);
    } finally {
      setUpdatingItems(prev => {
        const next = { ...prev };
        itemIdsToRemove.forEach(id => { delete next[id]; });
        return next;
      });
    }
  }, [getCustomerId, getGuestCart, saveGuestCart, fetchCart, cartData, resolveCartItemIdentifier, resolveFrequentlyId]);

  const validateCouponWithAPI = useCallback(async (code) => {
    const trimmedCode = code.trim().toUpperCase();
    const token = localStorage.getItem('authToken');
    const cartTotal = subtotal;

    try {
      const response = await fetch(
        `https://evercorenutrition.com/ev_admin/api/promo/validate/?code=${encodeURIComponent(trimmedCode)}&cart_total=${cartTotal}`,
        {
          method: 'GET',
          headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        }
      );

      const data = await response.json();

      if (response.status === 404) {
        return { valid: false, message: 'Invalid coupon code.' };
      }

      if (data.valid === true) {
        let discountAmt = 0;
        if (data.discount_type === 'percentage') {
          discountAmt = (cartTotal * data.discount_value) / 100;
          if (data.max_discount_amount && discountAmt > data.max_discount_amount) {
            discountAmt = data.max_discount_amount;
          }
        } else {
          discountAmt = data.discount_value;
        }

        // const applyOn = data.apply_on || data.target_type || 'cart';
          const applyOn = String(
            data.apply_on ||
            data.target_type ||
            ''
          ).trim().toLowerCase();
        return {
          valid: true,
          coupon: {
            code: data.code,
            discount_type: data.discount_type,
            discount_value: data.discount_value,
            title: data.title,
            description: data.description,
            discount_amount: discountAmt,
            min_order_amount: data.min_order_amount,
            max_discount_amount: data.max_discount_amount,
            remaining_uses: data.remaining_uses,
            apply_on: applyOn,
            apply_on_display: data.apply_on_display || (applyOn === 'combo' ? 'All Combos' : applyOn === 'product' ? 'All Products' : 'Whole Cart'),
          },
          message: data.message,
        };
      } else {
        return { valid: false, message: data.error || 'Invalid coupon code' };
      }
    } catch (err) {
      return { valid: false, message: 'Network error. Please try again.' };
    }
  }, [subtotal]);

  const applyCoupon = useCallback(async () => {
    const trimmed = couponCode.trim().toUpperCase();
    if (!trimmed) { setCouponError('Please enter a coupon code.'); return; }

    setCouponError('');
    setCouponLoading(true);
    const validation = await validateCouponWithAPI(trimmed);

    if (validation.valid) {
      const coupon = validation.coupon;
      const isValidForCart = isCouponValidForCart(coupon, cartData?.items);
      const isMinAmountMet = isCouponMinAmountMet(coupon, subtotal);

      if (!isMinAmountMet) {
        setCouponError(`Minimum order amount of ₹${(coupon.min_order_amount || 0).toFixed(2)} required.`);
        setAppliedCoupon(null);
        sessionStorage.removeItem('appliedCoupon');
        setCouponLoading(false);
        return;
      }

if (!isValidForCart) {

  const applyOn = String(
    validation.coupon.apply_on ||
    validation.coupon.target_type ||
    ''
  ).trim().toLowerCase();

  console.log("CURRENT APPLY ON:", applyOn);

  if (applyOn === 'combo') {

    setCouponError(
      'This coupon is only applicable on combo cart.'
    );

  } else if (applyOn === 'product') {

    setCouponError(
      'This coupon is only applicable on product cart.'
    );

  } else if (
    applyOn === 'cart' ||
    applyOn === 'mixed'
  ) {

    setCouponError(
      'This coupon is only applicable on mixed cart.'
    );

  } else {

    setCouponError(
      'This coupon is not applicable to your current cart.'
    );
  }

  setAppliedCoupon(null);
  sessionStorage.removeItem('appliedCoupon');
  setCouponLoading(false);

  return;
}

      setAppliedCoupon(coupon);
      sessionStorage.setItem('appliedCoupon', JSON.stringify(coupon));
      setCouponError('');
      setCouponCode('');
      setShowAvailableCoupons(false);
    } else {
      setAppliedCoupon(null);
      sessionStorage.removeItem('appliedCoupon');
      setCouponError(validation.message);
    }
    setCouponLoading(false);
  }, [couponCode, validateCouponWithAPI, cartData, subtotal, isCouponValidForCart, isCouponMinAmountMet]);

  const applyCouponFromList = useCallback((coupon) => {
    const isValidForCart = isCouponValidForCart(coupon, cartData?.items);
    const isMinAmountMet = isCouponMinAmountMet(coupon, subtotal);

    if (!isMinAmountMet) {
      setCouponError(`Minimum order amount of ₹${(coupon.min_order_amount || 0).toFixed(2)} required.`);
      return;
    }

    if (!isValidForCart) {
      if (coupon.apply_on === 'combo') {
        setCouponError('This coupon is applicable only on combo items.');
      } else if (coupon.apply_on === 'product') {
        setCouponError('This coupon is applicable only on products.');
      } else {
        setCouponError('This coupon is not applicable to your current cart items.');
      }
      return;
    }

    setCouponCode(coupon.code);
    setTimeout(() => applyCoupon(), 100);
  }, [applyCoupon, cartData, subtotal, isCouponValidForCart, isCouponMinAmountMet]);

  const removeCoupon = useCallback(() => {
    setAppliedCoupon(null);
    sessionStorage.removeItem('appliedCoupon');
    setCouponCode('');
    setCouponError('');
  }, []);

  const isCouponApplicable = useCallback((coupon) => {
    if (coupon.discount_value <= 0) return false;
    return subtotal >= (coupon.min_order_amount || 0);
  }, [subtotal]);

const usableCoupons = useMemo(() => {

  if (!availableCoupons.length || !cartData?.items?.length) {
    return [];
  }

  return availableCoupons.filter(coupon => {

    // minimum amount check
    const meetsMinAmount =
      subtotal >= (coupon.min_order_amount || 0);

    if (!meetsMinAmount) {
      return false;
    }

    // cart validation
    const validCart =
      isCouponValidForCart(coupon, cartData.items);

    if (!validCart) {
      return false;
    }

    return true;

  });

}, [
  availableCoupons,
  cartData,
  subtotal,
  isCouponValidForCart
]);
  // Get only usable coupons (that meet both min amount and cart type requirements)
  // const usableCoupons = useMemo(() => {
  //   if (!availableCoupons.length || !cartData?.items?.length) return [];
    
  //   return availableCoupons.filter(coupon => {
  //     // Check minimum order amount
  //     const meetsMinAmount = subtotal >= (coupon.min_order_amount || 0);
  //     if (!meetsMinAmount) return false;
      
  //     // Check if coupon is valid for cart items
  //     const isValidForCart = isCouponValidForCart(coupon, cartData.items);
  //     if (!isValidForCart) return false;
      
  //     return true;
  //   });
  // }, [availableCoupons, subtotal, cartData, isCouponValidForCart]);
  

  // FIXED: handleLoginSuccess - merges cart and then redirects to checkout if pending
  const handleLoginSuccess = useCallback(async (user) => {
    setShowLoginPopup(false);
    
    const token = localStorage.getItem('authToken');
    const customerId = getCustomerId();
    
    if (token && customerId) {
      // Show loading while merging
      setLoading(true);
      
      // Merge guest cart with user cart
      const mergeResult = await mergeGuestCartWithUserCart(customerId, token);
      console.log('Merge result:', mergeResult);
      
      // Refresh cart data
      await fetchCart();
      
      setLoading(false);
      
      // If checkout was pending, redirect to checkout
      if (pendingCheckout) {
        setPendingCheckout(false);
        // Small delay to ensure cart is fully updated
        setTimeout(() => {
          navigate('/cart');
        }, 500);
      }
    }
    
    window.dispatchEvent(new Event('cartUpdated'));
  }, [getCustomerId, mergeGuestCartWithUserCart, fetchCart, pendingCheckout, navigate]);

  // FIXED: handleCheckout - shows login popup for guests, sets pending flag
  const handleCheckout = useCallback((e) => {
    if (e) e.preventDefault();
    
    if (!isAuthenticated()) {
      setPendingCheckout(true);
      setShowLoginPopup(true);
    } else {
      navigate('/checkout');
    }
  }, [isAuthenticated, navigate]);

  // Event listeners
  useEffect(() => {
    const handleCartUpdate = () => {
      fetchCart();
    };
    
    window.addEventListener('cartUpdated', handleCartUpdate);
    
    const handleStorageChange = (e) => {
      if (e.key === 'cartUpdated' || e.key === 'guest_cart') {
        fetchCart();
      }
    };
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('cartUpdated', handleCartUpdate);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [fetchCart]);

  // Fetch free shipping rules
  useEffect(() => {
    const fetchFreeShipping = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(
          'https://evercorenutrition.com/ev_admin/api/shipping/free/',
          {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {},
          }
        );
        if (response.ok) {
          const data = await response.json();
          setFreeShippingData(data);
        }
      } catch (err) {
        console.error('Error fetching free shipping:', err);
      }
    };
    fetchFreeShipping();
  }, []);

  useEffect(() => {
    fetchCart();
    fetchAvailableCoupons();
  }, [fetchCart, fetchAvailableCoupons]);

  // Check for guest cart merge on mount (when user is already logged in)
  useEffect(() => {
    const checkAndMergeGuestCart = async () => {
      const token = localStorage.getItem('authToken');
      const customerId = getCustomerId();
      const guestCart = getGuestCart();
      
      if (token && customerId && guestCart && guestCart.length > 0) {
        await mergeGuestCartWithUserCart(customerId, token);
        await fetchCart();
      }
    };
    
    checkAndMergeGuestCart();
  }, [getCustomerId, mergeGuestCartWithUserCart, fetchCart, getGuestCart]);

  // Recalculate shipping when subtotal changes
  useEffect(() => {
    if (subtotal > 0) {
      if (freeShippingThreshold && subtotal >= freeShippingThreshold) {
        setShippingCharge(0);
      } else {
        calculateShipping(subtotal);
      }
    } else {
      setShippingCharge(0);
    }
  }, [subtotal, calculateShipping, freeShippingThreshold]);

  // FIXED: Auto-remove coupon when cart changes (quantity update, item removal, etc.)
  useEffect(() => {
    if (!loading && cartData) {
      checkAndAutoRemoveCoupon();
    }
  }, [subtotal, cartData, loading, checkAndAutoRemoveCoupon]);

  const getItemImage = (item) => {
    if (item.item_type === 'combo' && item.combo_image) {
      return item.combo_image.startsWith('http') ? item.combo_image : `${BASE}${item.combo_image}`;
    }
    const imageUrl = item.variant_image || item.product_image || item.image;
    if (!imageUrl) return '/assets/images/Yeast_Protein.png';
    return imageUrl.startsWith('http') ? imageUrl : `${BASE}${imageUrl}`;
  };

  const getItemDisplayName = (item) => {
    if (item.item_type === 'combo') return item.combo_name || item.name || 'Combo Offer';
    const parts = [];
    if (item.product_name) parts.push(item.product_name);
    if (item.name && !item.product_name) parts.push(item.name);
    if (item.variant_flavour && item.variant_flavour !== 'Default') parts.push(item.variant_flavour);
    if (item.variant_weight_display) parts.push(item.variant_weight_display);
    return parts.join(' - ');
  };

  const getFrequentlyBoughtItemName = (item) => {
    return item.product_name || item.name || 'Product';
  };

  const getFrequentlyBoughtItemDetails = (item) => {
    return [
      item.variant_flavour || item.variant_name,
      item.variant_weight_display,
    ].filter(value => value && value !== 'Default').join(' - ');
  };

  const getItemMrp = (item) => {
    if (item.item_type === 'combo') {
      if (item.combo_items_list && item.combo_items_list.length > 0) {
        return item.combo_items_list.reduce((sum, ci) => sum + (ci.mrp * ci.quantity), 0);
      }
      return null;
    }
    return item.variant_mrp;
  };

  const cartItemsForDisplay = useMemo(() => {
    const groups = new Map();
    const displayItems = [];

    (cartData?.items || []).forEach(item => {
      const frequentlyId = resolveFrequentlyId(item);
      if (!frequentlyId) {
        displayItems.push({ item, items: null });
        return;
      }

      let group = groups.get(frequentlyId);
      if (!group) {
        group = { item, items: [] };
        groups.set(frequentlyId, group);
        displayItems.push(group);
      }
      group.items.push(item);
    });

    return displayItems;
  }, [cartData, resolveFrequentlyId]);

  return (
    <>
      <Navbar />

      <section className="breadcrumb-section">
        <div className="container">
          <div className="breadcrumb-content">
            <h2>Shopping Cart</h2>
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb">
                <li className="breadcrumb-item"><a href="/">Home</a></li>
                <li className="breadcrumb-item active" aria-current="page">Cart</li>
              </ol>
            </nav>
          </div>
        </div>
      </section>

      <section className="cart-section" style={{marginBottom:"10px"}}>
        <div className="container">
          {loading && (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading your cart...</p>
            </div>
          )}

          {!loading && error && (
            <div className="error-state">
              <FaShoppingCart className="error-icon" />
              <h3>{error}</h3>
              <a href="/shop" className="btn btn-primary">Continue Shopping</a>
            </div>
          )}

          {!loading && !error && cartData && cartData.items.length === 0 && (
            <div className="empty-cart">
              <FaShoppingCart className="empty-cart-icon" />
              <h3>Your cart is empty</h3>
              <p>Looks like you haven't added anything to your cart yet</p>
              <a href="/shop" className="btn btn-primary">Start Shopping</a>
            </div>
          )}

          {!loading && !error && cartData && cartData.items.length > 0 && (
            <div className="cart-layout">
              <div className="cart-items-section">
                <h3 className="section-title">Cart Items ({cartItemCount})</h3>
                <div className="cart-items-list">
                  {cartItemsForDisplay.map(({ item, items: frequentlyItems }, index) => {
                    const isFrequentlyBought = Boolean(frequentlyItems);
                    const displayName = isFrequentlyBought
                      ? item.combo_snapshot?.bundle_name || 'Frequently Bought Package'
                      : getItemDisplayName(item);
                    let unitPrice = item.unit_price;
                    if (typeof unitPrice !== 'number' || isNaN(unitPrice) || unitPrice <= 0) {
                      unitPrice = parseFloat(item.price || item.selling_price || 0);
                    }
                    const validUnitPrice = (typeof unitPrice === 'number' && !isNaN(unitPrice) && unitPrice > 0) ? unitPrice : 0;
                    const itemMrp = getItemMrp(item);
                    const itemTotal = isFrequentlyBought
                      ? frequentlyItems.reduce((total, frequentlyItem) => {
                        const price = parseFloat(frequentlyItem.unit_price || frequentlyItem.price || 0);
                        return total + (Number.isNaN(price) ? 0 : price * frequentlyItem.quantity);
                      }, 0)
                      : validUnitPrice * item.quantity;
                    const isUpdating = isFrequentlyBought
                      ? frequentlyItems.some(frequentlyItem => updatingItems[frequentlyItem.id])
                      : updatingItems[item.id];
                    const isCombo = item.item_type === 'combo';
                    const bundleImage = item.combo_snapshot?.bundle_image || item.combo_snapshot?.combo_image || item.bundle_image || item.combo_image;
                    const isBundleCartItem = !isFrequentlyBought && Boolean(
                      item.frequently_id ||
                      item.bundle_id ||
                      item.bundle_name ||
                      item.is_bundle ||
                      item.is_frequently_bought ||
                      item.item_type === 'combo'
                    );

                    return (
                      <div className={`cart-item ${isCombo ? 'combo-item-card' : ''}`} key={item.id || index}>
                        <div className="item-image">
                          {isFrequentlyBought ? (
                            <div className="frequently-bought-main-image" aria-label={displayName}>
                              {bundleImage ? (
                                <img
                                  src={bundleImage.startsWith('http') ? bundleImage : `${BASE}${bundleImage}`}
                                  alt={displayName}
                                  onError={(e) => { e.currentTarget.src = getItemImage(item); }}
                                />
                              ) : frequentlyItems.slice(0, 2).map(frequentlyItem => (
                                <img
                                  key={frequentlyItem.id}
                                  src={getItemImage(frequentlyItem)}
                                  alt={getItemDisplayName(frequentlyItem)}
                                  onError={(e) => { e.currentTarget.src = '/assets/images/Yeast_Protein.png'; }}
                                />
                              ))}
                            </div>
                          ) : (
                            <a href={isCombo ? `/combo/${item.combo_id}` : `/product/${item.product_id}`}>
                              <img
                                src={getItemImage(item)}
                                alt={displayName}
                                style={{ width: '100%', height: '100%', objectFit: 'contain', objectPosition: 'center', padding: '4px' }}
                                onError={(e) => { e.target.src = '/assets/images/Yeast_Protein.png'; }}
                              />
                            </a>
                          )}
                          {isCombo && (
                            <div className="combo-badge">
                              <FaLayerGroup size={12} />
                              <span>COMBO</span>
                            </div>
                          )}
                        </div>

                        <div className="item-details">
                          <div className="item-header">
                            <a href={isCombo ? `/combo/${item.combo_id}` : `/product/${item.product_id}`} className="item-name">
                              {displayName}
                            </a>
                          </div>

                          {isFrequentlyBought && (
                            <div style={{ margin: '6px 0 10px' }}>
                              {frequentlyItems.map(frequentlyItem => (
                                <div key={frequentlyItem.id} style={{ fontSize: '13px', color: 'black', marginBottom: '6px' }}>
                                  <div>{getFrequentlyBoughtItemName(frequentlyItem)}</div>
                                  {getFrequentlyBoughtItemDetails(frequentlyItem) && (
                                    <div style={{ color: 'green', fontSize: '11px', marginTop: '2px' }}>
                                      {getFrequentlyBoughtItemDetails(frequentlyItem)}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}

                          {!isFrequentlyBought && !isCombo && (item.variant_name || item.variant_weight_display) && (
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                              {item.variant_name && item.variant_name !== 'Default' && (
                                <span style={{ fontSize: '12px', color: '#666', background: '#f0f0f0', padding: '2px 8px', borderRadius: '4px' }}>
                                  {item.variant_name}
                                </span>
                              )}
                              {item.variant_weight_display && (
                                <span style={{ fontSize: '12px', color: '#666', background: '#f0f0f0', padding: '2px 8px', borderRadius: '4px' }}>
                                  {item.variant_weight_display}
                                </span>
                              )}
                            </div>
                          )}

                          {!isFrequentlyBought && (
                            <div className="item-price">
                              <span className="current-price">₹{Math.round(validUnitPrice).toLocaleString('en-IN')}</span>
                              {itemMrp && itemMrp > validUnitPrice && (
                                <span className="original-price">₹{Math.round(parseFloat(itemMrp)).toLocaleString('en-IN')}</span>
                              )}
                            </div>
                          )}

                          <div className="item-actions">
                            {isBundleCartItem ? (
                              <div className="quantity-control bundle-locked-qty" style={{ pointerEvents: 'none', opacity: 0.9 }}>
                                <span className="qty-value" style={{ padding: '0 12px', minWidth: '54px', textAlign: 'center' }}>{item.quantity}</span>
                              </div>
                            ) : (
                              <div className="quantity-control">
                                <button
                                  className="qty-btn"
                                  onClick={() => item.quantity <= 1
                                    ? removeItem(item.id)
                                    : updateQuantity(item.id, item.quantity - 1)}
                                  disabled={(!isFrequentlyBought && item.quantity <= 1) || isUpdating}
                                >−</button>
                                <span className="qty-value">{item.quantity}</span>
                                <button
                                  className="qty-btn"
                                  onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                  disabled={isUpdating}
                                >+</button>
                              </div>
                            )}

                            <div className="item-total">
                              <span className="total-label">{isFrequentlyBought ? 'Package Total:' : 'Total:'}</span>
                              <span className="total-value">₹{Math.round(itemTotal).toLocaleString('en-IN')}</span>
                            </div>

                            <button
                              className="remove-btn"
                              onClick={() => removeItem(item.id)}
                              disabled={isUpdating}
                            >
                              {isUpdating ? <FaSpinner className="spinner" /> : <FaTrash />}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="order-summary-section">
                <div className="summary-card">
                  <h3 className="summary-title">Order Summary</h3>
                  
                  {/* Coupon auto-removal warning message */}
                  {couponError && couponError.includes('Minimum order amount') && (
                    <div className="coupon-warning-banner" style={{
                      display: "flex", alignItems: "center", gap: "8px", padding: "10px 16px",
                      borderRadius: "8px", background: "#fff3cd", border: "1px solid #ffeeba",
                      color: "#856404", marginBottom: "15px", fontSize: "13px"
                    }}>
                      <span>⚠️</span>
                      <span>{couponError}</span>
                    </div>
                  )}
                  
                  {freeShippingData && qualifiesForFreeShipping && (
                    <div className="free-shipping-banner success" style={{
                      display: "flex", alignItems: "center", gap: "8px", padding: "10px 16px",
                      borderRadius: "8px", background: "linear-gradient(90deg, #f0fdf4, #e6f7ed)",
                      border: "1px solid #d1f0d6", color: "#15803d", fontWeight: 600, fontSize: "14px"
                    }}>
                      <FaShippingFast style={{ color: "#15803d" }} />
                      <span>✓ Free shipping applied!</span>
                    </div>
                  )}

                  {freeShippingData && !qualifiesForFreeShipping && amountToFreeShipping > 0 && (
                    <div className="free-shipping-banner warning" style={{
                      display: "flex", alignItems: "center", gap: "12px", padding: "12px 16px",
                      borderRadius: "8px", background: "linear-gradient(90deg, #eff6ff, #e0f2fe)",
                      border: "1px solid #bfdbfe", color: "#1e40af", fontWeight: 500, fontSize: "14px"
                    }}>
                      <FaTruck style={{ color: "#2c00f1", fontSize: "18px" }} />
                      <div style={{ flex: 1 }}>
                        Add ₹{amountToFreeShipping.toFixed(2)} more to get Free Shipping!
                      </div>
                    </div>
                  )}

                  <div className="coupon-section">
                    <h4>Have a coupon?</h4>
                    {appliedCoupon ? (
                      <div className="applied-coupon">
                        <div className="coupon-info">
                          <FaTag className="coupon-icon" />
                          <div>
                            <span className="coupon-code">{appliedCoupon.code}</span>
                            <span className="coupon-discount">
                              {appliedCoupon.discount_type === 'percentage'
                                ? `${appliedCoupon.discount_value}% OFF`
                                : `₹${appliedCoupon.discount_value} OFF`}
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={removeCoupon}
                          style={{
                            backgroundColor: "black",
                            color: "white",
                            border: "none",
                            borderRadius: "50%",
                            width: "24px",
                            height: "24px",
                            cursor: "pointer",
                            fontSize: "16px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="coupon-input-group">
                          <input
                            type="text"
                            placeholder="Enter coupon code"
                            value={couponCode}
                            onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponError(''); }}
                            onKeyDown={(e) => e.key === 'Enter' && applyCoupon()}
                            disabled={couponLoading}
                          />
                          <button onClick={applyCoupon} disabled={couponLoading}>
                            {couponLoading ? <FaSpinner className="spinner" /> : 'Apply'}
                          </button>
                        </div>
                        {couponError && !couponError.includes('Minimum order amount') && (
                          <p className="coupon-error">{couponError}</p>
                        )}
                      </>
                    )}
                  </div>

                  {/* Available Coupons Section - Only show usable coupons */}
                  {usableCoupons.length > 0 && !appliedCoupon && (
                    <div className="available-coupons-section">
                      <button 
                        className="available-coupons-toggle"
                        onClick={() => setShowAvailableCoupons(!showAvailableCoupons)}
                      >
                       
                        <span>Available Offers & Coupons ({usableCoupons.length})</span>
                        {showAvailableCoupons ? <FaChevronUp /> : <FaChevronDown />}
                      </button>
                      
                      {showAvailableCoupons && (
                        <div className="available-coupons-list">
                          {usableCoupons.map((coupon, idx) => (
                            <div key={idx} className="available-coupon-item">
                              <div className="coupon-info">
                                <div className="coupon-code-block">
                                  <span className="coupon-code-text">{coupon.code}</span>
                                  {coupon.apply_on && coupon.apply_on !== 'cart' && (
                                    <span className="coupon-badge">
                                      {coupon.apply_on === 'combo' ? 'Combos Only' : 'Products Only'}
                                    </span>
                                  )}
                                </div>
                                <div className="coupon-details">
                                  <span className="coupon-value">
                                    {coupon.discount_type === 'percentage' 
                                      ? `${coupon.discount_value}% OFF` 
                                      : `₹${coupon.discount_value} OFF`}
                                  </span>
                                  {coupon.min_order_amount && coupon.min_order_amount > 0 && (
                                    <span className="coupon-min-order">
                                      Min. order ₹{coupon.min_order_amount}
                                    </span>
                                  )}
                                  
                                </div>
                              </div>
                              <button
                                className="apply-coupon-btn"
                                onClick={() => applyCouponFromList(coupon)}
                              >
                                Apply
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="price-breakdown">
                    <div className="price-row">
                      <span>Subtotal</span>
                      <span>₹{Math.round(subtotal).toLocaleString('en-IN')}</span>
                    </div>
                    {appliedCoupon && (
                      <div className="price-row discount">
                        <span>Discount</span>
                        <span>- ₹{Math.round(discountAmount).toLocaleString('en-IN')}</span>
                      </div>
                    )}
                    <div className="price-row">
                      <span>Shipping</span>
                      <span>
                        {loadingShipping
                          ? <FaSpinner className="spinner small" />
                          : shippingCharge === 0 && qualifiesForFreeShipping ? 'FREE' : `₹${shippingCharge.toFixed(2)}`}
                      </span>
                    </div>
                    <div className="price-row total">
                      <span>Total</span>
                      <span className="total-amount">₹{Math.round(total).toLocaleString('en-IN')}</span>
                    </div>
                  </div>

                  <button 
                    className="checkout-btn" 
                    onClick={handleCheckout}
                  >
                    Proceed to Checkout
                  </button>
                  <a href="/shop" className="continue-shopping"><FaArrowLeft /> Continue Shopping</a>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {showLoginPopup && (
        <div className="login-popup-overlay" onClick={() => {
          setShowLoginPopup(false);
          setPendingCheckout(false);
        }}>
          <div className="login-popup-container" onClick={(e) => e.stopPropagation()}>
            <button className="login-popup-close" onClick={() => {
              setShowLoginPopup(false);
              setPendingCheckout(false);
            }}>
              <i className="fa-solid fa-times"></i>
            </button>
            <Login setOpen={setShowLoginPopup} onLoginSuccess={handleLoginSuccess} />
          </div>
        </div>
      )}

      <Footer/>

      <style>{`
        .breadcrumb-section { padding: 40px 0 20px; background: #f8f9fa; }
        .breadcrumb-content { text-align: center; }
        .breadcrumb-content h2 { font-size: 32px; font-weight: 600; margin-bottom: 10px; }
        .breadcrumb { display: flex; justify-content: end; gap: 10px; list-style: none; padding: 0; }
        .breadcrumb-item a { color: #666; text-decoration: none; }
        .breadcrumb-item.active { color: #000; }
      
        .container { max-width: 1200px; margin: 0 auto; padding: 0 20px; }
        .loading-state { text-align: center; padding: 60px 20px; }
        .spinner { width: 40px; height: 40px; border: 3px solid #f3f3f3; border-top: 3px solid #000; border-radius: 50%; animation: spin 1s linear infinite; margin: 0 auto 20px; }
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        .error-state, .empty-cart { text-align: center; padding: 60px 20px; }
        .error-icon, .empty-cart-icon { font-size: 64px; color: #ddd; margin-bottom: 20px; }
        .error-state h3, .empty-cart h3 { font-size: 24px; margin-bottom: 10px; }
        .error-state p, .empty-cart p { color: #666; margin-bottom: 20px; }
        .btn-primary { display: inline-block; padding: 12px 30px; background: #000; color: white; text-decoration: none; border-radius: 5px; font-weight: 500; }
        .cart-layout { display: grid; grid-template-columns: 1fr 380px; gap: 30px; }
        .cart-items-section { background: white; border-radius: 10px; padding: 25px; box-shadow: 0 2px 10px rgba(0,0,0,0.5); }
        .section-title { font-size: 20px; font-weight: 600; margin-bottom: 20px; padding-bottom: 15px; border-bottom: 1px solid #eee; }
        .cart-items-list { display: flex; flex-direction: column; gap: 20px; }
        .cart-item { display: flex; gap: 20px; padding: 20px; background: #f9f9f9; border-radius: 8px; transition: all 0.3s; position: relative; border: 1px solid #e0dfdf; box-shadow: 1px 3px 12px #e0e0e0; }
        .cart-item:hover { background: white; box-shadow: 0 5px 15px rgba(0,0,0,0.08); }
        .item-image { width: 100px; height: 100px; flex-shrink: 0; border-radius: 8px; overflow: hidden; background: #f8f9fa; position: relative; display: flex; align-items: center; justify-content: center; }
        .item-image img { width: 100%; height: 100%; object-fit: contain; object-position: center; transition: transform 0.3s ease; }
        .item-image a { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; }
        .item-image:hover img { transform: scale(1.05); }
        .frequently-bought-main-image { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; gap: 2px; padding: 5px; }
        .frequently-bought-main-image img { width: 50%; height: 100%; object-fit: contain; background: #fff; }
        .frequently-bought-main-image img:only-child { width: 100%; }
        .combo-badge { position: absolute; top: 5px; left: 5px; background: rgba(0,0,0,0.7); color: white; padding: 2px 6px; border-radius: 4px; font-size: 10px; display: flex; align-items: center; gap: 3px; }
        .item-details { flex: 1; display: flex; flex-direction: column; gap: 10px; }
        .item-name { font-size: 16px; font-weight: 600; color: #333; text-decoration: none; }
        .item-name:hover { color: #000; }
        .item-price { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .current-price { font-size: 18px; font-weight: 700; color: #000; }
        .original-price { font-size: 14px; color: #999; text-decoration: line-through; }
        .item-actions { display: flex; align-items: center; gap: 20px; flex-wrap: wrap; }
        .quantity-control { display: flex; align-items: center; border: 1px solid #ddd; border-radius: 5px; overflow: hidden; }
        .qty-btn { width: 35px; height: 35px; background: #f8f9fa; border: none; font-size: 18px; cursor: pointer; transition: all 0.2s; }
        .qty-btn:hover:not(:disabled) { background: #e9ecef; }
        .qty-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .qty-value { width: 45px; text-align: center; font-weight: 600; }
        .item-total { display: flex; align-items: center; gap: 8px; }
        .total-label { font-size: 14px; color: #666; }
        .total-value { font-size: 18px; font-weight: 700; color: #333; }
        .remove-btn { background: none; border: none; color: #dc3545; font-size: 16px; cursor: pointer; padding: 8px; border-radius: 5px; transition: all 0.2s; }
        .remove-btn:hover:not(:disabled) { background: #fff0f0; }
        .remove-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .order-summary-section { position: sticky; top: 100px; align-self: start; }
        .summary-card { background: white; border-radius: 10px; padding: 25px; box-shadow: 0 2px 10px rgba(0,0,0,0.5); }
        .summary-title { font-size: 20px; font-weight: 600; margin-bottom: 20px; padding-bottom: 15px; border-bottom: 1px solid #eee; }
        .coupon-section { margin-bottom: 25px; }
        .coupon-section h4 { font-size: 14px; font-weight: 600; margin-bottom: 12px; color: #666; }
        .coupon-input-group { display: flex; gap: 10px; }
        .coupon-input-group input { flex: 1; padding: 12px 15px; border: 1px solid #ddd; border-radius: 5px; font-size: 14px; outline: none; }
        .coupon-input-group input:focus { border-color: #494949; box-shadow: 0 0 0 2px rgba(0,0,0,0.1); }
        .coupon-input-group button { padding: 0 20px; background: #000; color: white; border: none; border-radius: 5px; font-weight: 600; cursor: pointer; min-width: 80px; }
        .coupon-input-group button:disabled { opacity: 0.6; cursor: not-allowed; }
        .coupon-error { margin-top: 8px; font-size: 12px; color: #dc3545; }
        .applied-coupon { display: flex; align-items: center; justify-content: space-between; padding: 12px 15px; background: #e8f5e9; border: 1px solid #c3e6cb; border-radius: 5px; }
        .coupon-info { display: flex; align-items: center; gap: 10px; }
        .coupon-icon { color: #000; }
        .coupon-code { font-weight: 700; color: #000; margin-right: 8px; }
        .coupon-discount { font-size: 12px; background: #000; color: white; padding: 2px 8px; border-radius: 12px; margin-right: 8px; }

        /* Available Coupons Styles */
        .available-coupons-section {
          margin: 20px 0;
          border-top: 1px solid #eee;
          padding-top: 15px;
        }
        .available-coupons-toggle {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 0;
          background: none;
          border: none;
          cursor: pointer;
          font-size: 14px;
          font-weight: 600;
          color: #333;
          transition: all 0.2s;
        }
        .available-coupons-toggle:hover {
          color: #000;
        }
        .toggle-icon {
          margin-right: 8px;
          color: #000;
        }
        .available-coupons-list {
          margin-top: 12px;
          display: flex;
          flex-direction: column;
          gap: 12px;
          max-height: 400px;
          overflow-y: auto;
          padding-right: 5px;
        }
        .available-coupon-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px;
          background: #f9f9f9;
          border-radius: 8px;
          border: 1px solid #e0e0e0;
          transition: all 0.2s;
        }
        .available-coupon-item:hover {
          background: #fff;
          border-color: #000;
        }
        .available-coupon-item .coupon-info {
          flex: 1;
          flex-direction: column;
          align-items: flex-start;
          gap: 6px;
        }
        .coupon-code-block {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }
        .coupon-code-text {
          font-weight: 700;
          color: #000;
          font-size: 14px;
          letter-spacing: 0.5px;
        }
        .coupon-badge {
          font-size: 10px;
          background: #000;
          color: white;
          padding: 2px 6px;
          border-radius: 4px;
        }
        .coupon-details {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 8px;
        }
        .coupon-value {
          font-size: 13px;
          font-weight: 600;
          color: #15803d;
        }
        .coupon-min-order {
          font-size: 11px;
          color: #666;
        }
        .coupon-description {
          font-size: 11px;
          color: #888;
          margin: 0;
          width: 100%;
        }
        .apply-coupon-btn {
          padding: 6px 16px;
          background: #000;
          color: white;
          border: none;
          border-radius: 5px;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          white-space: nowrap;
        }
        .apply-coupon-btn:hover {
          background: #333;
          transform: translateY(-1px);
        }

        .price-breakdown { margin: 20px 0; padding: 15px 0; border-top: 1px solid #eee; border-bottom: 1px solid #eee; }
        .price-row { display: flex; justify-content: space-between; margin-bottom: 12px; font-size: 15px; }
        .price-row.discount { color: #000; }
        .price-row.total { margin-top: 15px; padding-top: 15px; border-top: 2px solid #eee; font-size: 18px; font-weight: 700; }
        .total-amount { color: #000; }
        .spinner.small { width: 14px; height: 14px; border-width: 2px; }
        .checkout-btn { display: block; width: 100%; padding: 15px; background: #000; color: white; text-align: center; text-decoration: none; border-radius: 5px; font-weight: 600; margin-bottom: 10px; border: none; cursor: pointer; font-size: 16px; }
        .checkout-btn:hover { background: #333; }
        .continue-shopping { display: flex; align-items: center; justify-content: center; gap: 8px; color: #666; text-decoration: none; font-size: 14px; }
        .continue-shopping:hover { color: #000; }

        .login-popup-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(255, 255, 255, 0.7);
          backdrop-filter: blur(5px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
          animation: fadeIn 0.3s ease;
        }

        .login-popup-container {
          position: relative;
          max-width: 500px;
          width: 90%;
          max-height: 90vh;
          overflow-y: auto;
          background: transparent;
          border-radius: 20px;
          animation: slideUp 0.3s ease;
        }

        .login-popup-close {
          position: absolute;
          top: 15px;
          right: 15px;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: white;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10;
          font-size: 16px;
          color: #333;
          transition: all 0.2s ease;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        .login-popup-close:hover {
          background: #f0f0f0;
          transform: scale(1.1);
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slideUp {
          from {
            transform: translateY(30px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @media (max-width: 992px) {
          .cart-layout { grid-template-columns: 1fr; }
          .order-summary-section { position: static; max-width: 500px; margin: 0 auto; }
        }
        @media (max-width: 768px) {
          .cart-item { flex-direction: column; }
          .item-image { width: 100%; height: 200px; }
          .item-actions { flex-direction: column; align-items: flex-start; }
          .quantity-control { width: 100%; }
          .qty-btn { flex: 1; }
          .qty-value { flex: 2; }
          .item-total { width: 100%; justify-content: space-between; }
          .remove-btn { align-self: flex-end; }
          .coupon-input-group { flex-direction: column; }
          .available-coupon-item {
            flex-direction: column;
            align-items: stretch;
            gap: 10px;
          }
          .apply-coupon-btn {
            width: 100%;
            padding: 8px;
          }
        }
        @media (max-width: 576px) {
          .cart-items-section, .summary-card { padding: 15px; }
          .section-title { font-size: 18px; }
          .item-name { font-size: 15px; }
          .current-price { font-size: 16px; }
        }
      `}</style>
    </>
  );
};

export default Cart;